import api from './api';
import { getFileNameFromContentDisposition } from '../utils/contentDisposition';
import { downloadBlob } from '../utils/downloadBlob';
import { mergeImpresionLetra } from '../constants/sucVentaImpresion';
import type { ImpresionMotor, ImpresionVariante } from '../constants/sucVentaImpresion';
import tipoFormularioService, { TIPO_FORMULARIO_MAX_PAGE_SIZE } from './tipoFormularioService';

export interface SucVentaImpresionLetra {
    motor: ImpresionMotor;
    variantePlantilla?: ImpresionVariante | string | null;
    rutaRdlc?: string | null;
}

export interface SucVentaCatalogoOpcion {
    valor: string;
    etiqueta: string;
}

export interface SucVentaCatalogoImpresion {
    motores: SucVentaCatalogoOpcion[];
    variantesPlantilla: SucVentaCatalogoOpcion[];
}

export interface SucVenta {
    nroSuc: number;
    nroTipoFormulario: string;
    formuA?: number;
    formuB?: number;
    formuC?: number;
    formuN?: number;
    numPropio?: string;
    nroTipoFormularioNumerador?: string;
    copias?: number;
    renglones?: number;
    plantiA?: string;
    plantiB?: string;
    plantiC?: string;
    plantiN?: string;
    plantiComun?: string;
    impresionA?: SucVentaImpresionLetra;
    impresionB?: SucVentaImpresionLetra;
    impresionC?: SucVentaImpresionLetra;
    impresionN?: SucVentaImpresionLetra;
    impresionComun?: SucVentaImpresionLetra;
    impresionEtiquetas?: SucVentaImpresionLetra;
    impA?: string;
    impB?: string;
    impC?: string;
    impN?: string;
    impComun?: string;
    vpreviaA: string;
    vpreviaB: string;
    vpreviaC: string;
    vpreviaN: string;
    vpreviaComun: string;
    imprime: string;
    liquidaComision: string;
    afectaLimCred?: string;
    actualizaLimCred?: string;
    tipoImpresora?: string;
    usaCodigoBarra?: number;
    tipoRenglones?: string;
    idWsfe?: string;
    generaRemito: string;
    nroSucRemito?: number;
    soloAdvertencia?: number;
    usaLetraM?: number;
    idWsfecred?: string;
    passEmision?: string;
    passAnulacion?: string;
    generaOcarga: string;
    nroSucOcarga?: number;
    notasAlPie?: string;
    notasEncabezado?: string;
    tipoFormulario?: any;
    nombreSucursal?: string;
}

export interface SucVentaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    nroSuc?: number;
    nroTipoFormulario?: string;
}

export interface SucVentaPagedResult {
    items: SucVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface AplicarTodosTiposFormularioResult {
    creados: number;
    omitidos: number;
    errores: { nroTipoFormulario: string; message: string }[];
}

const SUC_VENTA_PAGE_SIZE = 20;

const defaultImpresionLetra = () => mergeImpresionLetra(undefined, {}, null);

export const buildDefaultSucVenta = (nroSuc: number, nroTipoFormulario: string): SucVenta => ({
    nroSuc,
    nroTipoFormulario: nroTipoFormulario.trim(),
    formuA: 0,
    formuB: 0,
    formuC: 0,
    formuN: 0,
    numPropio: 'S',
    nroTipoFormularioNumerador: '',
    copias: 1,
    renglones: 10,
    plantiA: '',
    plantiB: '',
    plantiC: '',
    plantiN: '',
    plantiComun: '',
    impA: '',
    impB: '',
    impC: '',
    impN: '',
    impComun: '',
    impresionA: defaultImpresionLetra(),
    impresionB: defaultImpresionLetra(),
    impresionC: defaultImpresionLetra(),
    impresionN: defaultImpresionLetra(),
    impresionComun: defaultImpresionLetra(),
    impresionEtiquetas: defaultImpresionLetra(),
    vpreviaA: 'N',
    vpreviaB: 'N',
    vpreviaC: 'N',
    vpreviaN: 'N',
    vpreviaComun: 'N',
    imprime: 'N',
    liquidaComision: 'N',
    afectaLimCred: 'N',
    actualizaLimCred: 'N',
    tipoImpresora: '',
    usaCodigoBarra: 0,
    tipoRenglones: '',
    idWsfe: '',
    generaRemito: 'N',
    nroSucRemito: 0,
    soloAdvertencia: 0,
    usaLetraM: 0,
    idWsfecred: '',
    passEmision: '',
    passAnulacion: '',
    generaOcarga: 'N',
    nroSucOcarga: 0,
    notasAlPie: '',
    notasEncabezado: '',
});

async function fetchAllTipoFormularios() {
    const items: Awaited<ReturnType<typeof tipoFormularioService.getTipoFormularios>>['items'] = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
        const result = await tipoFormularioService.getTipoFormularios({
            pageNumber,
            pageSize: TIPO_FORMULARIO_MAX_PAGE_SIZE,
        });
        items.push(...(result.items || []));
        totalPages = result.totalPages || 1;
        pageNumber += 1;
    } while (pageNumber <= totalPages);

    return items;
}

async function fetchSucVentasBySucursal(nroSuc: number) {
    const items: SucVenta[] = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
        const response = await api.get<SucVentaPagedResult>('/api/SucVenta', {
            params: {
                PageNumber: pageNumber,
                PageSize: SUC_VENTA_PAGE_SIZE,
                NroSuc: nroSuc,
            },
        });
        const result = response.data;
        items.push(...(result.items || []));
        totalPages = result.totalPages || 1;
        pageNumber += 1;
    } while (pageNumber <= totalPages);

    return items;
}

const sucVentaService = {
    getSucVentas: async (params: SucVentaSearchParams): Promise<SucVentaPagedResult> => {
        const queryParams: Record<string, string | number | undefined> = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
            SearchTerm: params.searchTerm || undefined
        };
        if (params.nroSuc != null) queryParams.NroSuc = params.nroSuc;
        if (params.nroTipoFormulario != null) queryParams.NroTipoFormulario = params.nroTipoFormulario;
        const response = await api.get<SucVentaPagedResult>('/api/SucVenta', { params: queryParams });
        return response.data;
    },

    getSucVenta: async (nroSuc: number, nroTipoFormulario: string): Promise<SucVenta> => {
        const response = await api.get<SucVenta>(`/api/SucVenta/${nroSuc}/${nroTipoFormulario}`);
        return response.data;
    },

    createSucVenta: async (sucVenta: SucVenta): Promise<SucVenta> => {
        const response = await api.post<SucVenta>('/api/SucVenta', sucVenta);
        return response.data;
    },

    updateSucVenta: async (nroSuc: number, nroTipoFormulario: string, sucVenta: SucVenta): Promise<void> => {
        await api.put(`/api/SucVenta/${nroSuc}/${nroTipoFormulario}`, sucVenta);
    },

    deleteSucVenta: async (nroSuc: number, nroTipoFormulario: string): Promise<void> => {
        await api.delete(`/api/SucVenta/${nroSuc}/${nroTipoFormulario}`);
    },

    getCatalogoImpresion: async (): Promise<SucVentaCatalogoImpresion> => {
        const response = await api.get<SucVentaCatalogoImpresion>('/api/SucVenta/catalogo-impresion');
        return response.data;
    },

    uploadRdlc: async (
        archivo: File,
        params: { idCompania?: string; nroSuc?: number },
    ): Promise<{ rutaRdlc: string; nombreArchivo: string }> => {
        const formData = new FormData();
        formData.append('archivo', archivo);
        const query: Record<string, string | number> = {};
        const idCompania = params.idCompania?.trim();
        if (idCompania) query.idCompania = idCompania;
        if (params.nroSuc != null && params.nroSuc > 0) query.nroSuc = params.nroSuc;

        const response = await api.post<{ rutaRdlc: string; nombreArchivo: string }>(
            '/api/SucVenta/upload-rdlc',
            formData,
            {
                params: query,
                headers: { 'Content-Type': 'multipart/form-data' },
            },
        );
        return response.data;
    },

    downloadRdlc: async (rutaRdlc: string): Promise<void> => {
        const response = await api.get('/api/SucVenta/download-rdlc', {
            params: { ruta: rutaRdlc.trim() },
            responseType: 'blob',
        });

        const cd = response.headers['content-disposition'] as string | undefined;
        const fileName =
            getFileNameFromContentDisposition(cd) ||
            rutaRdlc.replace(/\\/g, '/').split('/').pop() ||
            'informe.rdlc';

        downloadBlob(response.data as Blob, fileName, 'application/xml');
    },

    aplicarTodosTiposFormulario: async (nroSuc: number): Promise<AplicarTodosTiposFormularioResult> => {
        const [tiposFormulario, sucVentasExistentes] = await Promise.all([
            fetchAllTipoFormularios(),
            fetchSucVentasBySucursal(nroSuc),
        ]);

        const tiposAsignados = new Set(
            sucVentasExistentes
                .map((sv) => String(sv.nroTipoFormulario ?? '').trim())
                .filter(Boolean),
        );

        const tiposPendientes = tiposFormulario.filter((tipo) => {
            const nroTipo = String(tipo.nroTipoFormulario ?? '').trim();
            return nroTipo && !tiposAsignados.has(nroTipo);
        });

        const result: AplicarTodosTiposFormularioResult = {
            creados: 0,
            omitidos: tiposAsignados.size,
            errores: [],
        };

        for (const tipo of tiposPendientes) {
            const nroTipoFormulario = String(tipo.nroTipoFormulario ?? '').trim();
            try {
                await sucVentaService.createSucVenta(buildDefaultSucVenta(nroSuc, nroTipoFormulario));
                result.creados += 1;
            } catch (error: any) {
                const data = error?.response?.data;
                const message =
                    typeof data === 'string'
                        ? data
                        : data?.error ?? data?.message ?? data?.Message ?? 'Error al crear asignación';
                result.errores.push({ nroTipoFormulario, message });
            }
        }

        return result;
    },
};

export default sucVentaService;
